/**
 * Comparative Analytics Module Configuration
 *
 * Multi-asset comparison with various visualization modes.
 * Enables benchmarking and performance comparison across the fleet.
 */

import { ModuleConfig } from './moduleTypes'

export const compareConfig: ModuleConfig = {
  id: 'compare',
  name: 'Comparative Analytics',
  description: 'Multi-asset comparison and benchmarking',
  icon: 'GitCompare',
  basePath: '/compare',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'select',
      path: '',
      name: 'Asset Selection',
      icon: 'CheckSquare',
      description: 'Select assets for comparison',
      component: 'AssetSelector',
      requiredPermissions: ['assets:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Device Selection',
        financial: 'System Selection',
        transportation: 'Vehicle Selection',
        iot_telecom: 'SIM Selection',
      },
    },
    {
      id: 'side-by-side',
      path: 'side-by-side',
      name: 'Side-by-Side',
      icon: 'Columns',
      description: 'View assets in parallel columns',
      component: 'SideBySideView',
      requiredPermissions: ['assets:read'],
      position: 2,
    },
    {
      id: 'overlay',
      path: 'overlay',
      name: 'Overlay View',
      icon: 'Layers',
      description: 'Overlay metrics on shared charts',
      component: 'OverlayView',
      requiredPermissions: ['assets:read'],
      position: 3,
    },
    {
      id: 'table',
      path: 'table',
      name: 'Table View',
      icon: 'Table',
      description: 'Tabular comparison of all metrics',
      component: 'ComparisonTable',
      requiredPermissions: ['assets:read'],
      position: 4,
    },
    {
      id: 'benchmark',
      path: 'benchmark',
      name: 'Fleet Benchmark',
      icon: 'Award',
      description: 'Compare against fleet averages and best performers',
      component: 'FleetBenchmark',
      requiredPermissions: ['analytics:read'],
      position: 5,
      industryLabels: {
        healthcare: 'Device Benchmark',
        financial: 'Portfolio Benchmark',
        transportation: 'Fleet Benchmark',
        iot_telecom: 'SIM Fleet Benchmark',
      },
    },
    {
      id: 'insights',
      path: 'insights',
      name: 'AI Insights',
      icon: 'Brain',
      description: 'AI-generated comparison insights and recommendations',
      component: 'ComparisonInsights',
      requiredPermissions: ['analytics:read'],
      position: 6,
    },
  ],

  workflows: [
    {
      id: 'performance-comparison',
      name: 'Performance Comparison',
      description: 'Systematic comparison of asset performance',
      icon: 'BarChart',
      steps: [
        {
          id: 'select-assets',
          name: 'Select Assets',
          description: 'Choose 2-5 assets to compare',
          navigateTo: 'select',
          icon: 'CheckSquare',
          estimatedDuration: 2,
        },
        {
          id: 'review-side-by-side',
          name: 'Side-by-Side Review',
          description: 'Compare key metrics visually',
          navigateTo: 'side-by-side',
          icon: 'Columns',
          estimatedDuration: 5,
        },
        {
          id: 'overlay-trends',
          name: 'Overlay Trends',
          description: 'Compare historical trends on shared timeline',
          navigateTo: 'overlay',
          icon: 'Layers',
          estimatedDuration: 5,
        },
        {
          id: 'analyze-differences',
          name: 'Analyze Differences',
          description: 'Identify key performance differences',
          navigateTo: 'table',
          icon: 'Table',
          estimatedDuration: 5,
        },
        {
          id: 'review-insights',
          name: 'Review AI Insights',
          description: 'Get AI-generated recommendations',
          navigateTo: 'insights',
          icon: 'Brain',
          estimatedDuration: 5,
        },
      ],
      triggerConditions: ['multipleAssetsSelected', 'benchmarkRequested'],
    },
    {
      id: 'best-performer-analysis',
      name: 'Best Performer Analysis',
      description: 'Identify and learn from top-performing assets',
      icon: 'Trophy',
      steps: [
        {
          id: 'identify-best',
          name: 'Identify Best Performers',
          description: 'Find top performers by key metrics',
          navigateTo: 'benchmark',
          icon: 'Award',
          estimatedDuration: 3,
        },
        {
          id: 'compare-to-average',
          name: 'Compare to Average',
          description: 'Understand what makes them stand out',
          navigateTo: 'table',
          icon: 'BarChart',
          estimatedDuration: 5,
        },
        {
          id: 'analyze-factors',
          name: 'Analyze Success Factors',
          description: 'Identify conditions that enable high performance',
          navigateTo: 'insights',
          icon: 'Lightbulb',
          estimatedDuration: 10,
        },
        {
          id: 'apply-learnings',
          name: 'Apply Learnings',
          description: 'Create action plan for underperformers',
          icon: 'Clipboard',
          estimatedDuration: 10,
        },
      ],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'overview',
        name: 'Comparison Overview',
        template: 'Comparing {assetCount} {assetPluralTerm}: {assetNames}. Reliability spread: {reliabilitySpread}% (best: {bestPerformer} at {bestScore}%, worst: {worstPerformer} at {worstScore}%).',
        order: 1,
        required: true,
      },
      {
        id: 'performance-gap',
        name: 'Performance Gap Analysis',
        template: 'Key performance gaps identified: {gapDescription}. Primary differentiator: {primaryDifferentiator}.',
        order: 2,
      },
      {
        id: 'best-practices',
        name: 'Best Practices',
        template: '**Learnings from {bestPerformer}:**\n• {learning1}\n• {learning2}\n• {learning3}',
        order: 3,
        condition: 'hasSignificantGap',
      },
      {
        id: 'recommendations',
        name: 'Recommendations',
        template: '**Recommendations:**\n1. {recommendation1}\n2. {recommendation2}\n3. {recommendation3}',
        order: 4,
        required: true,
      },
    ],
    availableVariables: [
      'assetCount', 'assetPluralTerm', 'assetNames', 'reliabilitySpread',
      'bestPerformer', 'bestScore', 'worstPerformer', 'worstScore',
      'gapDescription', 'primaryDifferentiator',
      'learning1', 'learning2', 'learning3',
      'recommendation1', 'recommendation2', 'recommendation3',
    ],
  },

  industryOverrides: {
    healthcare: {
      name: 'Device Comparison',
      description: 'Compare medical device performance and compliance',
      additionalSubPages: [
        {
          id: 'compliance-compare',
          path: 'compliance',
          name: 'Compliance Comparison',
          icon: 'Shield',
          description: 'Compare compliance and calibration status',
          component: 'ComplianceComparison',
          position: 7,
        },
      ],
    },
    financial: {
      name: 'Portfolio Benchmark',
      description: 'Compare system performance against benchmarks',
      icon: 'TrendingUp',
    },
    transportation: {
      name: 'Fleet Comparison',
      description: 'Compare vehicle performance and efficiency',
      additionalSubPages: [
        {
          id: 'driver-compare',
          path: 'drivers',
          name: 'Driver Comparison',
          icon: 'Users',
          description: 'Compare driver performance scores',
          component: 'DriverComparison',
          position: 7,
        },
      ],
    },
    iot_telecom: {
      name: 'SIM Comparison',
      description: 'Multi-IMSI carrier comparison and signal benchmarking',
      icon: 'GitCompare',
      additionalSubPages: [
        {
          id: 'carrier-benchmark',
          path: 'carriers',
          name: 'Carrier Benchmark',
          icon: 'Radio',
          description: 'Compare carrier performance across your SIM fleet',
          component: 'CarrierBenchmark',
          position: 7,
        },
        {
          id: 'signal-comparison',
          path: 'signal',
          name: 'Signal Comparison',
          icon: 'Signal',
          description: 'Compare signal quality metrics across SIMs',
          component: 'SignalComparison',
          position: 8,
        },
        {
          id: 'coverage-comparison',
          path: 'coverage',
          name: 'Coverage Comparison',
          icon: 'Map',
          description: 'Compare geographic coverage between carriers',
          component: 'CoverageComparison',
          position: 9,
        },
        {
          id: 'cost-comparison',
          path: 'costs',
          name: 'Cost Comparison',
          icon: 'DollarSign',
          description: 'Compare data rates and total cost of ownership',
          component: 'CostComparison',
          position: 10,
        },
        {
          id: 'latency-comparison',
          path: 'latency',
          name: 'Latency Comparison',
          icon: 'Timer',
          description: 'Compare network latency and round-trip times',
          component: 'LatencyComparison',
          position: 11,
        },
        {
          id: 'profile-comparison',
          path: 'profiles',
          name: 'Profile Comparison',
          icon: 'Layers',
          description: 'Compare eUICC profile performance',
          component: 'ProfileComparison',
          position: 12,
        },
      ],
      additionalWorkflows: [
        {
          id: 'carrier-evaluation',
          name: 'Carrier Evaluation',
          description: 'Comprehensive carrier comparison for optimal selection',
          icon: 'Award',
          steps: [
            {
              id: 'select-sims',
              name: 'Select SIMs to Compare',
              description: 'Choose SIMs across different carriers',
              navigateTo: 'select',
              icon: 'CheckSquare',
              estimatedDuration: 2,
            },
            {
              id: 'compare-signal',
              name: 'Compare Signal Quality',
              description: 'Analyze RSSI, RSRP, RSRQ across carriers',
              navigateTo: 'signal-comparison',
              icon: 'Signal',
              estimatedDuration: 5,
            },
            {
              id: 'compare-coverage',
              name: 'Compare Coverage',
              description: 'Evaluate geographic coverage differences',
              navigateTo: 'coverage-comparison',
              icon: 'Map',
              estimatedDuration: 5,
            },
            {
              id: 'compare-latency',
              name: 'Compare Latency',
              description: 'Measure network responsiveness',
              navigateTo: 'latency-comparison',
              icon: 'Timer',
              estimatedDuration: 5,
            },
            {
              id: 'compare-costs',
              name: 'Compare Costs',
              description: 'Analyze total cost of ownership',
              navigateTo: 'cost-comparison',
              icon: 'DollarSign',
              estimatedDuration: 5,
            },
            {
              id: 'review-insights',
              name: 'Review AI Insights',
              description: 'Get AI-powered carrier recommendations',
              navigateTo: 'insights',
              icon: 'Brain',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['newCarrierEvaluation', 'contractRenewal', 'performanceIssues'],
        },
        {
          id: 'multi-imsi-optimization',
          name: 'Multi-IMSI Optimization',
          description: 'Optimize profile distribution across your SIM fleet',
          icon: 'Cpu',
          steps: [
            {
              id: 'benchmark-profiles',
              name: 'Benchmark Active Profiles',
              description: 'Compare current profile performance',
              navigateTo: 'profile-comparison',
              icon: 'Layers',
              estimatedDuration: 5,
            },
            {
              id: 'analyze-carriers',
              name: 'Analyze Carrier Performance',
              description: 'Review carrier benchmark data',
              navigateTo: 'carrier-benchmark',
              icon: 'Radio',
              estimatedDuration: 5,
            },
            {
              id: 'identify-opportunities',
              name: 'Identify Switch Opportunities',
              description: 'Find SIMs that would benefit from profile switch',
              navigateTo: 'insights',
              icon: 'Lightbulb',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['monthlyOptimization', 'signalDegradation'],
        },
      ],
    },
    datacenter: {
      name: 'Infrastructure Comparison',
      description: 'Multi-rack and server comparison across facilities',
      icon: 'Server',
    },
  },

  requiredPermissions: ['assets:read'],
}
